import { useState, useEffect, useMemo } from "react";
import { useUrlParam, useUrlPanelSync } from '../hooks/useUrlParam';
import { fetchJobById } from '../lib/aiJobs';
import { supabase } from "../lib/supabase";
import { useUser } from "../context/UserContext";
import { formatDate } from "../utils/dateUtils";
import { zonedTimeToUtc } from 'date-fns-tz';
import { APP_TIMEZONE } from '../utils/dateUtils';
import Sidebar from "../components/layout/Sidebar";
import PageHeader from "../components/PageHeader";
import FulfillmentTaskList from "../components/fulfillment/FulfillmentTaskList";
import FulfillmentRail from "../components/fulfillment/FulfillmentRail";
import AiJobChips from "../components/ai/AiJobChips";
import type { AiJob } from "../lib/aiJobs";
import TaskDetailSidepanel from "../components/clientDetail/sections/functions/TaskDetailSidepanel";
import DeleteConfirmationModal from "../components/clientDetail/sections/functions/DeleteConfirmationModal";
import { faBoxArchive } from "@fortawesome/free-solid-svg-icons";
import confetti from "canvas-confetti";
import { DragEndEvent } from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import { Dropdown, SegmentedControl, StatCard } from "../components/ui";

interface Task {
  id: string;
  title: string;
  description?: string;
  status: string;
  due_date?: string;
  client_id?: string;
  client_name?: string;
  assigned_to?: string;
  priority: number;
  created_at: string;
  updated_at: string;
  video_url?: string;
}

interface User {
  id: string;
  name: string;
  email: string;
  role?: string;
}

interface TaskUpdatePayload {
  title: string;
  status: string;
  dueDate?: string | null;
  assignedTo?: string | null;
}

export default function FulfillmentHQ() {
  const { user } = useUser();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [isTaskSidepanelOpen, setIsTaskSidepanelOpen] = useState(false);
  // Agent queue job opened from the rail — same detail panel as the Agents screen
  const [openAiJob, setOpenAiJob] = useState<AiJob | null>(null);
  const [isReordering, setIsReordering] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  // Header ⋮ → Archive Submitted (bulk-archives the Submitted tasks in view)
  const [isArchiveSubmittedOpen, setIsArchiveSubmittedOpen] = useState(false);
  const [isArchivingSubmitted, setIsArchivingSubmitted] = useState(false);

  // Filtering state
  const [filterType, setFilterType] = useState<'overdue' | 'specific-date' | 'all'>('overdue');
  const [selectedDate, setSelectedDate] = useState(() => {
    // Default to today's date
    const today = new Date();
    return today.toISOString().split('T')[0];
  });

  const userId = user?.id;
  const userAgencyId = user?.agency_id;

  // Fetch users and set current user as default
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        let query = supabase
          .from("users")
          .select("id, name, email, role")
          .neq("role", "client")
          .order("name");

        // Filter by user's agency_id if user has one
        if (userAgencyId) {
          query = query.eq("agency_id", userAgencyId);
        }

        const { data, error } = await query;

        if (error) throw error;

        setUsers((data || []).map((u) => ({ ...u, name: u.name ?? u.email, role: u.role ?? undefined })));

        // Set current user as default selected user
        if (userId && !selectedUserId && !hasInitialized) {
          setSelectedUserId(userId);
          setHasInitialized(true);
        }
      } catch (err) {
        console.error("Error fetching users:", err);
        setError("Failed to load users. Please refresh the page.");
      }
    };

    fetchUsers();
  }, [userId, userAgencyId, hasInitialized, selectedUserId]);

  // Fetch tasks for selected user
  useEffect(() => {
    // Wait until the default user selection has been applied; fetching earlier
    // kicks off an "all users" query that can resolve after (and overwrite)
    // the user-scoped one.
    if (!hasInitialized) return;

    let cancelled = false;

    const fetchTasks = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const today = new Date();
        today.setHours(23, 59, 59, 999); // End of today for comparison

        let query = supabase
          .from("client_tasks")
          .select(
            `
            id,
            title,
            description,
            status,
            due_date,
            assigned_to,
            priority,
            created_at,
            updated_at,
            video_url,
            client_id,
            archived,
            archived_at,
            metadata,
            clients (
              name
            )
          `
          );

        query = query.eq('archived', false);

        // Only filter by assigned_to if a specific user is selected
        if (selectedUserId && selectedUserId !== '') {
          query = query.eq("assigned_to", selectedUserId);
        } else {
          // For "All Users", only show tasks that have someone assigned
          query = query.not("assigned_to", "is", null);
        }

        // Apply date filtering based on filterType
        if (filterType === 'overdue') {
          // Show tasks due today or before
          query = query.lte("due_date", today.toISOString());
        } else if (filterType === 'specific-date') {
          // Show tasks due on the specific selected date in Mountain Time
          // Parse the selectedDate string (YYYY-MM-DD) into components
          const [year, month, day] = selectedDate.split('-').map(Number);

          // Create start and end of day in Mountain Time, then convert to UTC
          const startOfDayMT = new Date(year, month - 1, day, 0, 0, 0, 0);
          const endOfDayMT = new Date(year, month - 1, day, 23, 59, 59, 999);

          // Convert Mountain Time to UTC for the database query
          const startOfDayUTC = zonedTimeToUtc(startOfDayMT, APP_TIMEZONE);
          const endOfDayUTC = zonedTimeToUtc(endOfDayMT, APP_TIMEZONE);

          query = query
            .gte("due_date", startOfDayUTC.toISOString())
            .lte("due_date", endOfDayUTC.toISOString());
        }
        // For 'all' filterType, we don't add any date filters

        const { data, error } = await query
          .order("priority");

        if (error) throw error;

        // Transform data to include client_name
        const transformedData = data.map((task) => ({
          ...task,
          client_name: task.clients?.name || "Unknown Client",
          priority: task.priority !== null ? task.priority : 0, // Ensure priority is set
        }));

        // Look up website information for each task
        const enhancedTasks = [];
        for (const task of transformedData) {
          // Check if the task has website_id in metadata
          const websiteId = (task.metadata as { website_id?: string } | null)?.website_id;
          if (websiteId) {
            try {
              // Fetch website data
              const { data: websiteData } = await supabase
                .from("websites")
                .select("id, title, url")
                .eq("id", websiteId)
                .single();

              if (websiteData) {
                enhancedTasks.push({
                  ...task,
                  website_id: websiteData.id,
                  website_title: websiteData.title || websiteData.url
                });
                continue;
              }
            } catch (error) {
              console.error("Error fetching website data:", error);
            }
          }
          // If no website data found or no website_id, just add the task as is
          enhancedTasks.push(task);
        }

        if (!cancelled) setTasks(enhancedTasks as unknown as Task[]);
      } catch (err) {
        console.error("Error fetching tasks:", err);
        if (!cancelled) setError("Failed to load tasks. Please try again.");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchTasks();

    return () => {
      cancelled = true;
    };
  }, [hasInitialized, selectedUserId, filterType, selectedDate]);

  const handleOpenTask = async (task: Task) => {
    try {
      // Fetch additional data if needed
      const enhancedTask = await fetchTemplateData(task);

      setSelectedTask(enhancedTask);
      setIsTaskSidepanelOpen(true);
    } catch (error) {
      console.error("Error preparing task details:", error);
      setError("Error loading task details. Please try again.");
    }
  };

  // The open task (?task=<id>) and agent job (?job=<id>) live in the URL, so refresh and back keep your place. A
  // task outside the loaded list (another day or person) is fetched by id.
  const [taskParam, setTaskParam] = useUrlParam('task');
  const [jobParam, setJobParam] = useUrlParam('job');
  useUrlPanelSync(
    taskParam,
    isTaskSidepanelOpen ? (selectedTask?.id ?? null) : null,
    !isLoading,
    (id) => {
      const listed = tasks.find((t) => t.id === id);
      if (listed) {
        void handleOpenTask(listed);
        return;
      }
      void supabase
        .from('client_tasks')
        .select('*')
        .eq('id', id)
        .maybeSingle()
        .then(({ data }) => {
          if (data) void handleOpenTask(data as unknown as Task);
        });
    },
    () => setIsTaskSidepanelOpen(false),
  );
  useUrlPanelSync(
    jobParam,
    openAiJob?.id ?? null,
    true,
    (id) => {
      void fetchJobById(id).then((job) => {
        if (job) setOpenAiJob(job);
      });
    },
    () => setOpenAiJob(null),
  );

  // Template data fetch removed - task_templates table no longer exists
  const fetchTemplateData = async (task: Task): Promise<Task> => {
    return task;
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!active || !over || active.id === over.id) {
      return;
    }

    const oldIndex = tasks.findIndex((task) => task.id === active.id);
    const newIndex = tasks.findIndex((task) => task.id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      console.error("Could not find task indexes", { oldIndex, newIndex });
      return;
    }

    // Create new array with updated order AND priorities
    const newOrderedTasks = arrayMove([...tasks], oldIndex, newIndex).map(
      (task, index) => ({
        ...task,
        priority: index,
      }),
    );

    // Update local state immediately
    setTasks(newOrderedTasks);

    setIsReordering(true);
    setError(null);

    try {
      // Create array of update promises
      const updatePromises = newOrderedTasks.map((task, index) => {
        return supabase
          .from("client_tasks")
          .update({ priority: index })
          .eq("id", task.id);
      });

      // Execute all updates in parallel
      await Promise.all(updatePromises);
    } catch (err) {
      console.error("Error updating task priorities:", err);
      setError("Failed to update task order. Please try again.");

      // Fetch fresh data from database on error
      if (selectedUserId) {
        const { data, error } = await supabase
          .from("client_tasks")
          .select(
            `
            id,
            title,
            description,
            status,
            due_date,
            priority,
            created_at,
            updated_at,
            video_url,
            client_id,
            clients (
              name
            )
          `,
          )
          .eq("assigned_to", selectedUserId)
          .order("priority");

        if (!error && data) {
          const transformedData = data.map((task) => ({
            ...task,
            client_name: task.clients?.name || "Unknown Client",
            priority: task.priority !== null ? task.priority : 0,
          }));
          setTasks(transformedData as unknown as Task[]);
        } else {
          console.error("Error fetching updated tasks:", error);
        }
      }
    } finally {
      setIsReordering(false);
    }
  };

  const handleStatusChange = async (taskId: string, newStatus: string) => {
    const task = tasks.find(t => t.id === taskId);
    const oldStatus = task?.status;

    try {
      const { error } = await supabase
        .from("client_tasks")
        .update({ status: newStatus })
        .eq("id", taskId);

      if (error) throw error;

      // Update local state
      setTasks(
        tasks.map((task) =>
          task.id === taskId ? { ...task, status: newStatus } : task,
        ),
      );

      // Update the selected task if it matches
      if (selectedTask?.id === taskId) {
        setSelectedTask({ ...selectedTask, status: newStatus });
      }

      // Fire confetti if task was just completed or submitted to the client
      if ((newStatus === "Complete" || newStatus === "Submitted") && oldStatus !== newStatus) {
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 },
          colors: ['#d95636', '#fbbf24', '#10b981', '#3b82f6', '#8b5cf6']
        });
      }
    } catch (err) {
      console.error("Error updating task status:", err);
      setError("Failed to update task status. Please try again.");
    }
  };

  // Handle updating task from sidepanel
  const handleUpdateTask = async (taskId: string, updatedTask: TaskUpdatePayload) => {
    try {
      // Get original task to compare status
      const originalTask = tasks.find(t => t.id === taskId);

      const updateData = {
        title: updatedTask.title,
        status: updatedTask.status,
        due_date: updatedTask.dueDate || null,
        assigned_to: updatedTask.assignedTo || null,
      };

      const { error } = await supabase
        .from("client_tasks")
        .update(updateData)
        .eq("id", taskId);

      if (error) throw error;

      // Update local state
      setTasks(
        tasks.map((task) =>
          task.id === taskId ? ({ ...task, ...updateData } as Task) : task,
        ),
      );

      // Update selected task
      if (selectedTask?.id === taskId) {
        setSelectedTask({ ...selectedTask, ...updateData } as Task);
      }

      // Check if status changed to Complete and send notification
      const statusChangedToComplete = originalTask?.status !== 'Complete' && updatedTask.status === 'Complete';

      if (statusChangedToComplete) {
        const assignedUser = users.find(u => u.id === updateData.assigned_to);
        const { data: { session } } = await supabase.auth.getSession();
        fetch('/.netlify/functions/task-complete-notification', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session?.access_token ?? ''}`,
          },
          body: JSON.stringify({
            task: {
              id: taskId,
              title: updatedTask.title,
              description: originalTask?.description,
              assigned_to_name: assignedUser?.name,
              due_date: updateData.due_date
            }
          })
        }).catch(err => console.error('Notification error:', err));
      }
      // The drawer autosaves and stays open; it toasts and reverts on a rejection.
    } catch (err) {
      console.error("Error updating task:", err);
      throw err;
    }
  };

  // Submitted tasks in the current view — exactly the rows the list is showing,
  // so the filters (person, date range) scope the archive
  const submittedTasks = useMemo(() => tasks.filter((t) => t.status === 'Submitted'), [tasks]);

  const handleArchiveSubmitted = async () => {
    if (isArchivingSubmitted) return;
    if (submittedTasks.length === 0) {
      // View changed under the open modal — nothing left to archive
      setIsArchiveSubmittedOpen(false);
      return;
    }
    const ids = submittedTasks.map((t) => t.id);
    setIsArchivingSubmitted(true);
    setError(null);
    try {
      const { error: archiveError } = await supabase
        .from('client_tasks')
        .update({ archived: true, archived_at: new Date().toISOString() })
        .in('id', ids);
      if (archiveError) throw archiveError;

      const archived = new Set(ids);
      setTasks((prev) => prev.filter((t) => !archived.has(t.id)));
      if (selectedTask && archived.has(selectedTask.id)) {
        setIsTaskSidepanelOpen(false);
        setSelectedTask(null);
      }
      setIsArchiveSubmittedOpen(false);
    } catch (err) {
      console.error('Error archiving submitted tasks:', err);
      setError('Failed to archive submitted tasks. Please try again.');
    } finally {
      setIsArchivingSubmitted(false);
    }
  };

  // Get selected user name for display
  const selectedUserName = useMemo(() => {
    if (!selectedUserId) return "All Users";
    const user = users.find(u => u.id === selectedUserId);
    return user?.name || "Unknown User";
  }, [selectedUserId, users]);

  // KPIs scoped to the selected person, computed from loaded tasks
  const kpis = useMemo(() => {
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const endOfToday = new Date();
    endOfToday.setHours(23, 59, 59, 999);

    let overdue = 0;
    let dueToday = 0;
    let review = 0;
    let blocked = 0;
    let oldestOverdue: Date | null = null;

    for (const t of tasks) {
      if (t.status === 'Complete') review++;
      if (t.status === 'Blocked') blocked++;
      if (t.due_date && t.status !== 'Complete' && t.status !== 'Submitted') {
        const due = new Date(t.due_date);
        if (due < startOfToday) {
          overdue++;
          if (!oldestOverdue || due < oldestOverdue) oldestOverdue = due;
        } else if (due <= endOfToday) {
          dueToday++;
        }
      }
    }

    return { overdue, dueToday, review, blocked, oldestOverdue };
  }, [tasks]);

  const visibleNotDone = tasks.filter((t) => t.status !== 'Complete' && t.status !== 'Submitted').length;
  const firstName = selectedUserName.split(' ')[0];

  return (
    <div style={{ minHeight: '100vh', background: 'var(--surface-canvas)', color: 'var(--text-body)' }}>
      <Sidebar />

      <main className="px-4 md:px-8 pb-8 md:ml-[72px]">
        <PageHeader
          title="Fulfillment HQ"
          count={visibleNotDone}
          actions={
            <span className="md:ml-[6px]">
              <Dropdown
                size="sm"
                options={[
                  { value: 'all', label: 'All Users' },
                  ...users.map(u => ({ value: u.id, label: u.name }))
                ]}
                value={selectedUserId || 'all'}
                onChange={(e) => setSelectedUserId(e.target.value === 'all' ? '' : e.target.value)}
                placeholder="Filter by user"
                style={{ width: '180px' }}
              />
            </span>
          }
        />

        {/* KPI strip */}
        <div
          className="grid grid-cols-2 lg:grid-cols-4"
          style={{ gap: 'var(--space-4)', marginBottom: 'var(--space-6)' }}
        >
          <StatCard
            label="Overdue"
            value={String(kpis.overdue)}
            delta={kpis.overdue > 0 ? 'needs attention' : undefined}
            deltaTone={kpis.overdue > 0 ? 'down' : undefined}
            hint={
              kpis.oldestOverdue
                ? `oldest from ${formatDate(kpis.oldestOverdue, 'MMM d')}`
                : 'all caught up'
            }
          />
          <StatCard
            label="Due Today"
            value={String(kpis.dueToday)}
            hint={selectedUserName === 'All Users' ? 'whole team' : `${firstName}'s queue`}
          />
          <StatCard label="Needs Review" value={String(kpis.review)} hint="complete, awaiting review" />
          <StatCard
            label="Blocked"
            value={String(kpis.blocked)}
            delta={kpis.blocked > 0 ? 'waiting' : undefined}
            deltaTone={kpis.blocked > 0 ? 'down' : undefined}
            hint={kpis.blocked > 0 ? 'waiting on externals' : 'nothing stuck'}
          />
        </div>

        {/* Filters row */}
        {/* Phones get short labels so the switcher stays one row; the date input wraps below it */}
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px', marginBottom: 'var(--space-5)' }}>
          <SegmentedControl
            options={[
              { id: 'overdue', label: <><span className="md:hidden">Due Now</span><span className="hidden md:inline">Due Today &amp; Before</span></> },
              { id: 'specific-date', label: <><span className="md:hidden">On Date</span><span className="hidden md:inline">Specific Date</span></> },
              { id: 'all', label: <><span className="md:hidden">All</span><span className="hidden md:inline">All Tasks</span></> },
            ]}
            value={filterType}
            onChange={(id) => setFilterType(id as 'overdue' | 'specific-date' | 'all')}
          />
          {/* On phones the date only shows once On Date is picked; otherwise it pushed a
              half-empty second row */}
          <input
            type="date"
            className={`ck-date-input${filterType === 'specific-date' ? '' : ' hidden md:block'}`}
            style={{ width: '150px', maxWidth: '100%', opacity: filterType === 'specific-date' ? 1 : 0.55 }}
            value={selectedDate}
            onChange={(e) => {
              setSelectedDate(e.target.value);
              setFilterType('specific-date');
            }}
          />
        </div>

        {error && (
          <div
            style={{
              padding: '10px 14px',
              borderRadius: 'var(--radius-md)',
              marginBottom: 'var(--space-4)',
              background: 'var(--status-down-soft)',
              border: '1px solid rgba(229, 72, 77, 0.25)',
              color: 'var(--danger-400)',
              fontSize: 'var(--text-sm)',
            }}
          >
            {error}
          </div>
        )}

        {/* queue + insight rail: 50/50 on wide screens, stacked below xl */}
        <div className="grid grid-cols-1 xl:grid-cols-2" style={{ gap: 'var(--space-5)', alignItems: 'start' }}>
          <FulfillmentTaskList
            tasks={tasks}
            isLoading={isLoading}
            isReordering={isReordering}
            selectedUserName={selectedUserName}
            selectedUserId={selectedUserId}
            users={users}
            handleOpenTask={(task: Task) => setTaskParam(task.id)}
            handleDragEnd={handleDragEnd}
            handleStatusChange={handleStatusChange}
            filterType={filterType}
            selectedDate={selectedDate}
            onArchiveSubmitted={() => setIsArchiveSubmittedOpen(true)}
          />
          <FulfillmentRail
            tasks={tasks}
            users={users}
            selectedUserId={selectedUserId}
            selectedUserName={selectedUserName}
            onOpenTask={(t) => {
              const full = tasks.find((x) => x.id === t.id);
              if (full) setTaskParam(full.id);
            }}
            onOpenJob={(job) => setJobParam(job.id)}
          />
        </div>

        {/* Task Detail Sidepanel */}
        <TaskDetailSidepanel
          isOpen={isTaskSidepanelOpen}
          onClose={() => setTaskParam(null)}
          selectedTask={selectedTask}
          users={users}
          onUpdateTask={handleUpdateTask}
          onArchiveTask={async (taskId) => {
            try {
              setError(null);

              // Archive in database
              const { error: archiveError } = await supabase
                .from('client_tasks')
                .update({ archived: true, archived_at: new Date().toISOString() })
                .eq('id', taskId);

              if (archiveError) throw archiveError;

              // Update local state
              setTasks(prevTasks => prevTasks.filter(t => t.id !== taskId));

              // Close the sidepanel
              setIsTaskSidepanelOpen(false);
              setSelectedTask(null);
              setTaskParam(null);

              return Promise.resolve();
            } catch (err) {
              console.error('Error archiving task:', err);
              setError('Failed to archive task. Please try again.');
              return Promise.reject(err);
            }
          }}
        />

        {/* Agent queue job detail — same panel the Agents screen opens */}
        {openAiJob ? (
          <AiJobChips
            key={openAiJob.id}
            job={openAiJob}
            panelOnly
            initialOpenJobId={openAiJob.id}
            onClosePanel={() => setJobParam(null)}
          />
        ) : null}

        {/* Archive Submitted — confirm before the bulk write */}
        <DeleteConfirmationModal
          isOpen={isArchiveSubmittedOpen}
          onClose={() => {
            if (!isArchivingSubmitted) setIsArchiveSubmittedOpen(false);
          }}
          onConfirm={handleArchiveSubmitted}
          isDeleting={isArchivingSubmitted}
          title="Archive submitted tasks?"
          message={
            submittedTasks.length === 1
              ? `This archives “${submittedTasks[0].title}” — the one task with status Submitted in the current view. It leaves Fulfillment HQ but stays on the client's archived list.`
              : `This archives ${submittedTasks.length} tasks with status Submitted in the current view (${selectedUserName}${
                  filterType === 'all' ? ', all tasks' : filterType === 'overdue' ? ', due today & before' : `, due ${selectedDate}`
                }). They leave Fulfillment HQ but stay on each client's archived list.`
          }
          confirmButtonLabel={submittedTasks.length === 1 ? 'Archive' : `Archive ${submittedTasks.length}`}
          processingLabel="Archiving…"
          titleIcon={faBoxArchive}
        />
      </main>
    </div>
  );
}
